import { createContext, useContext, useEffect, useState } from 'react';
import { CheckCircle, ShoppingCart } from 'shared/icons';
import { CART_GRID_COLS } from '../../constants/dimensions';
import { TBtn, TModal } from '../primitives';
import { useTokens } from '../tokens';
import { CartFooter } from './CartFooter';
import { CartHeader } from './CartHeader';
import { CartItemsTable } from './CartItemsTable';

const CartContext = createContext(null);

export function useCartContext() {
    const ctx = useContext(CartContext);
    if (!ctx) {
        throw new Error('Cart sub-componente deve ser usado dentro de <Cart>');
    }
    return ctx;
}

/**
 * Compound component pro carrinho de OC.
 *
 * Uso típico (CartModal preserva compat — renderiza tudo com layout default):
 *   <CartModal {...props} />
 *
 * Uso avançado (re-ordenar ou substituir seções):
 *   <Cart {...props}>
 *     <Cart.Header />
 *     <Cart.ItemsTable />
 *     <Cart.Footer />
 *   </Cart>
 */
export function Cart({ open, onClose, items, onQtyChange, onRemove, onClear, onConfirm, generating, children }) {
    const T = useTokens();
    const [confirmed, setConfirmed] = useState(false);

    useEffect(() => {
        if (!open) {
            setConfirmed(false);
        }
    }, [open]);

    const totalCost = items.reduce((s, i) => s + i.qty * (i.row.cost || 0), 0);
    const totalQty = items.reduce((s, i) => s + i.qty, 0);

    const value = {
        items,
        totalCost,
        totalQty,
        confirmed,
        setConfirmed,
        generating,
        onQtyChange,
        onRemove,
        onClear,
        onClose,
        onConfirm,
        T,
        CART_GRID_COLS,
    };

    return (
        <CartContext.Provider value={value}>
            <TModal
                open={open}
                onClose={onClose}
                width={720}
                title="Carrinho de compra"
                data-faid="Cart_Modal_Operacoes"
            >
                <CartBody>{children}</CartBody>
            </TModal>
        </CartContext.Provider>
    );
}

function CartBody({ children }) {
    const { items, confirmed, totalCost, totalQty, onClear, onClose, setConfirmed, T } = useCartContext();

    if (items.length === 0) {
        return (
            <div
                style={{
                    display: 'grid',
                    placeItems: 'center',
                    height: 180,
                    color: T.muted,
                    fontSize: 13,
                    fontFamily: T.sans,
                }}
            >
                <div style={{ textAlign: 'center', lineHeight: 2 }}>
                    <div style={{ marginBottom: 6, display: 'flex', justifyContent: 'center' }}>
                        <ShoppingCart size={32} color={T.muted} />
                    </div>
                    Nenhum item no carrinho
                </div>
            </div>
        );
    }

    if (confirmed) {
        return (
            <div style={{ display: 'grid', placeItems: 'center', height: 240, fontFamily: T.sans }}>
                <div style={{ textAlign: 'center', lineHeight: 1.8 }}>
                    <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center' }}>
                        <CheckCircle size={36} color={T.ok} />
                    </div>
                    <div style={{ fontSize: 16, fontWeight: 600, color: T.ink, marginBottom: 6 }}>
                        OC gerada com sucesso!
                    </div>
                    <div style={{ fontSize: 13, color: T.muted }}>
                        {totalQty} unidades · R$ {totalCost.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} custo
                        estimado
                    </div>
                    <div style={{ marginTop: 20, display: 'flex', gap: 8, justifyContent: 'center' }}>
                        <TBtn
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                                onClear();
                                setConfirmed(false);
                                onClose();
                            }}
                        >
                            Fechar
                        </TBtn>
                        <TBtn variant="default" size="sm" onClick={() => setConfirmed(false)}>
                            Ver carrinho
                        </TBtn>
                    </div>
                </div>
            </div>
        );
    }

    // Children customizado OU layout default
    if (children) return <>{children}</>;
    return (
        <>
            <CartHeader />
            <CartItemsTable />
            <CartFooter />
        </>
    );
}

Cart.Header = CartHeader;
Cart.ItemsTable = CartItemsTable;
Cart.Footer = CartFooter;

/** Backward-compat: callers existentes usam CartModal. */
export function CartModal(props) {
    return <Cart {...props} />;
}
